/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import {
    AfterViewInit,
    Component,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';

import {
    IAviDataGridConfig,
} from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';
import { TrafficLogCinematicStore } from '../../traffic-logs/traffic-log-cinematic/traffic-log-cinematic.store';
import { ITrafficLogsStates, TrafficLogsStore } from '../../traffic-logs/traffic-logs.store';
import { FilterOperatorType } from '../../../logs.types';

import './log-cinematic-section-ssl.component.less';

import * as l10n from './log-cinematic-section-ssl.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description
 *     Component for displaying SSL section in a traffic log cinematic view.
 * @author Kondiparthi Shanmukha Sarath, Zhiqian Liu
 */
@Component({
    selector: 'log-cinematic-section-ssl',
    templateUrl: './log-cinematic-section-ssl.component.html',
})
export class LogCinematicSectionSslComponent implements OnInit, AfterViewInit {
    /**
     * Datagrid template for the SSL version field.
     */
    @ViewChild('sslVersionFieldTemplateRef')
    public readonly sslVersionFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Datagrid template for the SSL certtificate type field.
     */
    @ViewChild('sslCertTypeFieldTemplateRef')
    public readonly sslCertTypeFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Datagrid template for the SNI hostname field.
     */
    @ViewChild('sniHostnameFieldTemplateRef')
    public readonly sniHostnameFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Datagrid template for the SSL Encryption Algorithm field.
     */
    @ViewChild('sslEncFieldTemplateRef')
    public readonly sslEncFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Datagrid template for the SSL Perfect Forward Secrecy field.
     */
    @ViewChild('sslPfsFieldTemplateRef')
    public readonly sslPfsFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Datagrid template for the Client Supplied Ciphers field.
     */
    @ViewChild('clientSuppliedCiphersFieldTemplateRef')
    public readonly clientSuppliedCiphersFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Datagrid template for the OCSP status field.
     */
    @ViewChild('oscpStatusFieldTemplateRef')
    public readonly oscpStatusFieldTemplateRef: TemplateRef<HTMLElement>;

    public readonly ssl$ = this.logCinematicStore.ssl$;
    public readonly vsAppProfileType$ = this.logsStore.vsAppProfileType$;

    public readonly FilterOperatorType = FilterOperatorType;

    public sslGridConfig: IAviDataGridConfig;

    public clientSuppliedCiphersGridConfig: IAviDataGridConfig;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    constructor(
        private readonly l10nService: L10nService,
        public readonly logCinematicStore: TrafficLogCinematicStore,
        private readonly logsStore: TrafficLogsStore<ITrafficLogsStates>,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * @override
     * Setting up SSL grid config for avi-data-grid usage.
     */
    public ngOnInit(): void {
        this.sslGridConfig = {
            fields: [],
            layout: {
                hideCheckboxes: true,
                hidePagination: true,
            },
        };

        this.clientSuppliedCiphersGridConfig = {
            fields: [],
            layout: {
                hideCheckboxes: true,
                hidePagination: true,
            },
        };
    }

    /**
     * @override
     * Setting fields for ssl grid config.
     */
    public ngAfterViewInit(): void {
        this.sslGridConfig.fields = [
            {
                label: this.l10nService.getMessage(globalL10nKeys.versionLabel),
                id: 'ssl-version',
                templateRef: this.sslVersionFieldTemplateRef,
            },
            {
                label: this.l10nService.getMessage(globalL10nKeys.typeLabel),
                id: 'ssl-cert-type',
                templateRef: this.sslCertTypeFieldTemplateRef,
            },
            {
                label: this.l10nService.getMessage(l10nKeys.sniHostnameLabel),
                id: 'sni-hostname',
                templateRef: this.sniHostnameFieldTemplateRef,
            },
            {
                label: this.l10nService.getMessage(l10nKeys.perfectForwardSecrecyLabel),
                id: 'ssl-pfs',
                templateRef: this.sslPfsFieldTemplateRef,
            },
            {
                label: this.l10nService.getMessage(l10nKeys.encryptionAlgorithmLabel),
                id: 'ssl-enc',
                templateRef: this.sslEncFieldTemplateRef,
            },
            {
                label: this.l10nService.getMessage(l10nKeys.oscpHandlingLabel),
                id: 'ocsp',
                templateRef: this.oscpStatusFieldTemplateRef,
            },
        ];

        this.clientSuppliedCiphersGridConfig.fields = [
            {
                label: this.l10nService.getMessage(l10nKeys.clientSuppliedCiphersLabel),
                id: 'client-supplied-ciphers',
                templateRef: this.clientSuppliedCiphersFieldTemplateRef,
            },
        ];
    }
}
